import { PaginationState } from '@tanstack/react-table';
import { DatePickerRange } from '../../components';
import {
  ApiPracticeProspectDetails,
  ApiSuccess,
  ApiTagTypes,
  CreatePracticeProspectPayload,
  CustomSortingState,
  EditPracticeProspectPayload,
  PracticeProspectDetails,
  PracticeProspectListResponse,
  PracticeProspectListResponseData,
  ProspectCategory,
  SimulationsFilterKeys,
  UpdatedPracticeProspectData,
} from '../../types';
import { parseProspect } from '../../utils';
import { apiSlice } from '../api.services';

interface GetPracticeProspectsPayload {
  [SimulationsFilterKeys.PROSPECT_ACCOUNT]?: string[];
  [SimulationsFilterKeys.PROSPECT]?: string[];
  [SimulationsFilterKeys.DATE_CREATED]?: DatePickerRange[];
  [SimulationsFilterKeys.SEARCH]?: string;
  [SimulationsFilterKeys.TAGS]?: string[];
  // Specifies the categories of prospects to include/exclude in the filters.
  [SimulationsFilterKeys.CATEGORY]?: ProspectCategory[];
  // Indicates whether to exclude the specified categories from the filters.
  excludeCategories?: boolean;
  isFavorited?: boolean;
  isDraft?: boolean;
  isForYouPage?: boolean;
  pagination?: PaginationState;
  sorting?: CustomSortingState;
}

interface ApplyTagsPayload {
  id: string;
  tagsToApply: string[];
  tagsToRemove: string[];
}

const enhancedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPracticeProspects: builder.mutation<PracticeProspectListResponseData, GetPracticeProspectsPayload>({
      query: ({ pagination, sorting, ...filters }) => ({
        url: '/prospects',
        method: 'POST',
        params: {
          page: pagination?.pageIndex,
          perPage: pagination?.pageSize,
          ...sorting,
        },
        body: filters,
      }),
      transformResponse: (response: PracticeProspectListResponse) => ({
        ...response.data,
        prospects: response.data.prospects.map(parseProspect),
      }),
    }),
    getPracticeProspectById: builder.mutation<PracticeProspectDetails, string>({
      query: (prospectId) => `/prospects/${prospectId}`,
      transformResponse: (response: ApiSuccess<{ prospect: ApiPracticeProspectDetails }>) =>
        parseProspect(response.data.prospect),
    }),
    applyTags: builder.mutation<ApiSuccess, ApplyTagsPayload>({
      query: ({ id, ...tagData }) => ({
        url: `/prospects/${id}/apply-tags`,
        method: 'POST',
        body: tagData,
      }),
      invalidatesTags: () => [ApiTagTypes.CALLS, ApiTagTypes.FOLDERS],
    }),
    regeneratePrompt: builder.mutation<ApiSuccess<UpdatedPracticeProspectData>, string>({
      query: (prospectId) => ({
        url: `/prospects/${prospectId}/regenerate-prompt`,
        method: 'POST',
      }),
    }),
    createPracticeProspect: builder.mutation<ApiSuccess<UpdatedPracticeProspectData>, CreatePracticeProspectPayload>({
      query: ({ orgId, ...prospectData }) => ({
        url: '/prospects/create',
        method: 'POST',
        params: { orgId },
        body: prospectData,
      }),
    }),
    editPracticeProspect: builder.mutation<ApiSuccess<UpdatedPracticeProspectData>, EditPracticeProspectPayload>({
      query: ({ id, ...prospectUpdates }) => ({
        url: `/prospects/${id}/edit`,
        method: 'POST',
        body: prospectUpdates,
      }),
      invalidatesTags: () => [ApiTagTypes.FOLDERS],
    }),
    archivePracticeProspect: builder.mutation<ApiSuccess<UpdatedPracticeProspectData>, string>({
      query: (id) => ({
        url: `/prospects/${id}/archive`,
        method: 'POST',
      }),
      invalidatesTags: () => [ApiTagTypes.FOLDERS],
    }),
    deletePracticeProspect: builder.mutation<ApiSuccess, string>({
      query: (id) => ({
        url: `/prospects/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [ApiTagTypes.FOLDERS],
    }),
    reactivatePracticeProspect: builder.mutation<ApiSuccess<UpdatedPracticeProspectData>, string>({
      query: (id) => ({
        url: `/prospects/${id}/reactivate`,
        method: 'POST',
      }),
      invalidatesTags: () => [ApiTagTypes.FOLDERS],
    }),
    favoriteProspect: builder.mutation<ApiSuccess, string>({
      query: (id) => ({
        url: `/prospects/${id}/favorite`,
        method: 'POST',
      }),
      invalidatesTags: () => [ApiTagTypes.FOLDERS],
    }),
    unfavoriteProspect: builder.mutation<ApiSuccess, string>({
      query: (id) => ({
        url: `/prospects/${id}/unfavorite`,
        method: 'POST',
      }),
      invalidatesTags: () => [ApiTagTypes.FOLDERS],
    }),
  }),
});

export const {
  useGetPracticeProspectsMutation,
  useGetPracticeProspectByIdMutation,
  useApplyTagsMutation,
  useRegeneratePromptMutation,
  useCreatePracticeProspectMutation,
  useEditPracticeProspectMutation,
  useArchivePracticeProspectMutation,
  useDeletePracticeProspectMutation,
  useReactivatePracticeProspectMutation,
  useFavoriteProspectMutation,
  useUnfavoriteProspectMutation,
} = enhancedApiSlice;
